import { Button, FormStatus, GlobalError, H2, P, Space } from '@dnb/eufemia';
import { type JSX, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { updateMcpGuide } from '@/api/api';
import Container from '@/components/Container';
import LoadingModal from '@/components/LoadingModal';
import MarkdownEditor from '@/components/MarkdownEditor';
import ModalPage from '@/components/ModalPage';
import { useBackend } from '@/hooks/useBackend';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { NotFoundPage } from '@/pages/NotFoundPage';

export default function EditMcpGuide(): JSX.Element {
  const { featureFlags } = useFeatureFlags();
  const navigate = useNavigate();
  const { api: mcpSlug, environment, version } = useParams<{ api: string; environment: string; version: string }>();

  const [content, setContent] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guidePath = mcpSlug && environment && version ? `/mcp-servers/${mcpSlug}/${environment}/${version}/guide` : '/';

  const { data: mcp, isLoading: mcpLoading } = useBackend(
    '/mcp/{mcpSlug}',
    featureFlags.ENABLE_MCP_PAGES && mcpSlug ? { params: { path: { mcpSlug } } } : null,
  );

  const { data: releaseData, isLoading: releaseLoading } = useBackend(
    '/mcp/{mcpSlug}/environments/{environmentSlug}/releases/{version}',
    featureFlags.ENABLE_MCP_PAGES && mcpSlug && environment && version
      ? { params: { path: { mcpSlug, environmentSlug: environment, version } } }
      : null,
  );

  useEffect(() => {
    if (!initialized && releaseData) {
      setContent(releaseData.guide ?? '');
      setInitialized(true);
    }
  }, [initialized, releaseData]);

  if (!featureFlags.ENABLE_MCP_PAGES) {
    return <NotFoundPage />;
  }

  const isLoading = mcpLoading || releaseLoading;

  const handleSave = async () => {
    if (!mcpSlug || !environment || !version) return;
    setSaving(true);
    setError(null);
    try {
      await updateMcpGuide(mcpSlug, environment, version, content);
      navigate(guidePath, { replace: true });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Something went wrong, please try again later.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPage
      onClose={() => navigate(guidePath, { replace: true })}
      skeleton={isLoading}
      subtitle={mcp?.name}
      title="Edit guide"
    >
      {saving && <LoadingModal />}
      <Container centered size="large">
        {!isLoading && (!mcp || !mcp.isOwner) ? (
          <GlobalError title="You do not have access to edit this guide" />
        ) : (
          <>
            <H2 bottom="small" top="0">
              Guide
            </H2>
            <P bottom="medium">The documentation guide for this MCP. Supports Markdown syntax for formatting.</P>
            <MarkdownEditor height={700} onChange={(value) => setContent(value ?? '')} value={content} />
            {error && <FormStatus state="error" stretch text={error} top="medium" />}
            <Space top="medium">
              <Button
                disabled={saving}
                icon="save"
                icon_position="left"
                on_click={handleSave}
                right="small"
                size="large"
              >
                Save guide
              </Button>
              <Button disabled={saving} on_click={() => navigate(guidePath)} size="large" variant="secondary">
                Cancel
              </Button>
            </Space>
          </>
        )}
      </Container>
    </ModalPage>
  );
}
